import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ComponentCard from '../../components/common/ComponentCard';
import Label from '../../components/form/Label';
import { api, apiEndpoints } from '../../utils/apiService';

// ── Types ──────────────────────────────────────────────────────────────────────

interface CampaignTypeConfig {
  id: number;
  key: string;
  label: string;
  description: string;
  search_instructions: string;
  required_evidence: string[];
  positive_examples: string[];
  negative_examples: string[];
  intro_examples: string[];
  body_examples: string[];
  example_subjects: string[];
  claim_safety_rules: string;
  claim_style: 'soft' | 'modeled_estimate' | 'audit_backed';
  default_confidence_threshold: number;
  revenue_relevant: boolean;
  is_active: boolean;
  sort_order: number;
  version: number;
}

export interface DripCampaignTemplateVariantConfig {
  id?: number;
  label: string;
  sort_order: number;
  subject_template: string;
  email_1_template: string;
  email_2_template: string;
  email_3_template: string;
  is_active: boolean;
  version?: number;
}

export interface DripCampaignTemplateConfig {
  id: number;
  name: string;
  campaign_type: string | null;
  campaign_type_label: string;
  variants: DripCampaignTemplateVariantConfig[];
  notes: string;
  is_active: boolean;
  version: number;
}

interface PlaybookConfig {
  id: number;
  scope_type: 'global' | 'market' | 'cuisine';
  scope_value: string;
  is_active: boolean;
  campaign_type_ranking: string[];
  enabled_overrides: Record<string, boolean>;
  seed_intros: string[];
  seed_bodies: string[];
  extra_instructions: string;
  version: number;
}

interface RevenueAssumptionConfig {
  id: number;
  tier: 'conservative' | 'moderate' | 'aggressive';
  playbook: number | null;
  missed_calls_per_month: number;
  high_value_inquiry_rate: string;
  average_inquiry_value: string;
  close_rate: string;
  estimated_annual_revenue_at_risk: number;
  is_active: boolean;
}

// ── Helpers ────────────────────────────────────────────────────────────────────

const inputClassName =
  'app-input min-h-10 w-full rounded-2xl px-3 py-2 text-sm';
const textAreaClassName = 'app-input w-full rounded-2xl px-3 py-2 text-sm';
const primaryButtonClassName =
  'inline-flex items-center justify-center rounded-2xl bg-[rgb(var(--app-brand))] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[rgb(var(--app-brand-strong))] disabled:cursor-not-allowed disabled:opacity-50';
const secondaryButtonClassName =
  'app-button-secondary inline-flex items-center justify-center rounded-2xl px-3 py-1.5 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50';

const listToText = (items: string[] | undefined): string =>
  (items || []).join('\n');
const textToList = (text: string): string[] =>
  text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

const CLAIM_STYLES = [
  { value: 'soft', label: 'Soft (no numeric claims)' },
  { value: 'modeled_estimate', label: 'Modeled estimate (framed as estimate)' },
  { value: 'audit_backed', label: 'Audit backed (cite audit numbers)' },
] as const;

// Mirrors ALLOWED_TEMPLATE_VARIABLES in the backend drip_templates service.
export const TEMPLATE_VARIABLES = [
  '${restaurant.name}',
  '${restaurant.restaurant_group_name}',
  '${restaurant.contact_name}',
  '${restaurant.contact_first_name}',
  '${restaurant.contact_last_name}',
  '${restaurant.contact_title}',
  '${restaurant.address}',
  '${restaurant.city}',
  '${restaurant.market}',
  '${restaurant.cuisine}',
  '${restaurant.neighborhood}',
  '${restaurant.website}',
  '${restaurant.phone}',
  '${audit.missed_calls}',
  '${audit.total_calls}',
  '${audit.answered_calls}',
  '${audit.missed_call_rate}',
  '${audit.avg_time_to_answer_ms}',
  '${audit.evidence_summary}',
  '${research.summary}',
  '${research.signal_strength}',
  '${selected_campaign.label}',
  '${selected_campaign.description}',
  '${selected_campaign.hook}',
];

const errorText = (err: unknown): string =>
  err instanceof Error ? err.message : 'Request failed';

async function requestJson<T>(
  method: 'get' | 'post' | 'patch',
  url: string,
  data?: unknown,
): Promise<T> {
  const response =
    method === 'get' ? await api.get(url) : await api[method](url, data);
  if (!response) {
    throw new Error('No response from server.');
  }
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    // Non-JSON response body.
  }
  if (!response.ok) {
    const detail =
      body && typeof body === 'object'
        ? JSON.stringify(body).substring(0, 200)
        : '';
    throw new Error(
      `Request failed (status ${response.status})${detail ? `: ${detail}` : ''}`,
    );
  }
  return body as T;
}

// ── Campaign type editor ───────────────────────────────────────────────────────

function CampaignTypeEditor({
  campaign,
  onSaved,
}: {
  campaign: CampaignTypeConfig;
  onSaved: () => void;
}) {
  const [form, setForm] = useState(() => ({
    label: campaign.label,
    description: campaign.description,
    search_instructions: campaign.search_instructions,
    required_evidence: listToText(campaign.required_evidence),
    positive_examples: listToText(campaign.positive_examples),
    negative_examples: listToText(campaign.negative_examples),
    intro_examples: listToText(campaign.intro_examples),
    body_examples: listToText(campaign.body_examples),
    example_subjects: listToText(campaign.example_subjects),
    claim_safety_rules: campaign.claim_safety_rules,
    claim_style: campaign.claim_style,
    default_confidence_threshold: String(campaign.default_confidence_threshold),
    revenue_relevant: campaign.revenue_relevant,
    is_active: campaign.is_active,
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const setField = (field: string, value: string | boolean) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      await requestJson(
        'patch',
        apiEndpoints.salesConfigCampaignType(campaign.id),
        {
          label: form.label,
          description: form.description,
          search_instructions: form.search_instructions,
          required_evidence: textToList(form.required_evidence),
          positive_examples: textToList(form.positive_examples),
          negative_examples: textToList(form.negative_examples),
          intro_examples: textToList(form.intro_examples),
          body_examples: textToList(form.body_examples),
          example_subjects: textToList(form.example_subjects),
          claim_safety_rules: form.claim_safety_rules,
          claim_style: form.claim_style,
          default_confidence_threshold:
            parseFloat(form.default_confidence_threshold) || 0,
          revenue_relevant: form.revenue_relevant,
          is_active: form.is_active,
        },
      );
      onSaved();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4 border-t border-[rgb(var(--app-border)/0.72)] pt-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <Label>Label</Label>
          <input
            className={inputClassName}
            value={form.label}
            onChange={(e) => setField('label', e.target.value)}
          />
        </div>
        <div>
          <Label>Claim style</Label>
          <select
            className={inputClassName}
            value={form.claim_style}
            onChange={(e) => setField('claim_style', e.target.value)}
          >
            {CLAIM_STYLES.map((style) => (
              <option key={style.value} value={style.value}>
                {style.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <Label>Description (when to use)</Label>
        <textarea
          className={textAreaClassName}
          rows={2}
          value={form.description}
          onChange={(e) => setField('description', e.target.value)}
        />
      </div>
      <div>
        <Label>
          Search instructions (what evidence the agent should hunt for)
        </Label>
        <textarea
          className={textAreaClassName}
          rows={3}
          value={form.search_instructions}
          onChange={(e) => setField('search_instructions', e.target.value)}
        />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <Label>Required evidence (signal keys, one per line)</Label>
          <p className="mb-1 text-xs text-gray-500 dark:text-gray-400">
            ANY one matching signal qualifies the campaign. Leave blank for no
            evidence gate.
          </p>
          <textarea
            className={textAreaClassName}
            rows={3}
            value={form.required_evidence}
            onChange={(e) => setField('required_evidence', e.target.value)}
          />
        </div>
        <div>
          <Label>Example subject lines (one per line)</Label>
          <textarea
            className={textAreaClassName}
            rows={3}
            value={form.example_subjects}
            onChange={(e) => setField('example_subjects', e.target.value)}
          />
        </div>
        <div>
          <Label>Example intros (one per line)</Label>
          <textarea
            className={textAreaClassName}
            rows={4}
            value={form.intro_examples}
            onChange={(e) => setField('intro_examples', e.target.value)}
          />
        </div>
        <div>
          <Label>Example bodies (one per line)</Label>
          <textarea
            className={textAreaClassName}
            rows={4}
            value={form.body_examples}
            onChange={(e) => setField('body_examples', e.target.value)}
          />
        </div>
        <div>
          <Label>Positive-fit examples (one per line)</Label>
          <textarea
            className={textAreaClassName}
            rows={3}
            value={form.positive_examples}
            onChange={(e) => setField('positive_examples', e.target.value)}
          />
        </div>
        <div>
          <Label>Negative-fit examples (one per line)</Label>
          <textarea
            className={textAreaClassName}
            rows={3}
            value={form.negative_examples}
            onChange={(e) => setField('negative_examples', e.target.value)}
          />
        </div>
      </div>
      <div>
        <Label>
          Claim safety rules (banned/unsafe claims, allowed framing)
        </Label>
        <textarea
          className={textAreaClassName}
          rows={2}
          value={form.claim_safety_rules}
          onChange={(e) => setField('claim_safety_rules', e.target.value)}
        />
      </div>
      <div className="flex flex-wrap items-center gap-6">
        <div className="w-40">
          <Label>Confidence threshold (0-1)</Label>
          <input
            className={inputClassName}
            type="number"
            min={0}
            max={1}
            step={0.05}
            value={form.default_confidence_threshold}
            onChange={(e) =>
              setField('default_confidence_threshold', e.target.value)
            }
          />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.revenue_relevant}
            onChange={(e) => setField('revenue_relevant', e.target.checked)}
          />
          Revenue relevant (agent may attach revenue-at-risk math)
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(e) => setField('is_active', e.target.checked)}
          />
          Enabled
        </label>
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <button
        type="button"
        className={primaryButtonClassName}
        onClick={save}
        disabled={saving}
      >
        {saving ? 'Saving…' : `Save ${campaign.key}`}
      </button>
    </div>
  );
}

// ── Drip campaign template editor ──────────────────────────────────────────────

const GENERIC_CAMPAIGN_VALUE = '';

const createEmptyVariant = (
  label: string,
  sortOrder: number,
): DripCampaignTemplateVariantConfig => ({
  label,
  sort_order: sortOrder,
  subject_template: '',
  email_1_template: '',
  email_2_template: '',
  email_3_template: '',
  is_active: true,
});

const emptyTemplateForm = {
  name: '',
  campaign_type: GENERIC_CAMPAIGN_VALUE,
  variants: [createEmptyVariant('A', 0), createEmptyVariant('B', 1)],
  notes: '',
  is_active: true,
};

const labelFromIndex = (index: number): string => {
  let value = index + 1;
  let label = '';
  while (value > 0) {
    value -= 1;
    label = String.fromCharCode(65 + (value % 26)) + label;
    value = Math.floor(value / 26);
  }
  return label;
};

const nextVariantLabel = (
  variants: DripCampaignTemplateVariantConfig[],
): string => {
  const usedLabels = new Set(
    variants.map((variant) => variant.label.trim().toUpperCase()),
  );
  let index = 0;
  while (usedLabels.has(labelFromIndex(index))) index += 1;
  return labelFromIndex(index);
};

const isValidVariant = (variant: DripCampaignTemplateVariantConfig) =>
  Boolean(
    variant.label.trim() &&
      variant.subject_template.trim() &&
      variant.email_1_template.trim(),
  );

function DripTemplateEditor({
  template,
  campaignTypes,
  onSaved,
  onDeleted,
  onCancel,
}: {
  template: DripCampaignTemplateConfig | null;
  campaignTypes: CampaignTypeConfig[];
  onSaved: () => void;
  onDeleted?: () => void;
  onCancel?: () => void;
}) {
  const [form, setForm] = useState(() =>
    template
      ? {
          name: template.name,
          campaign_type: template.campaign_type ?? GENERIC_CAMPAIGN_VALUE,
          variants: [...template.variants].sort(
            (a, b) => a.sort_order - b.sort_order,
          ),
          notes: template.notes,
          is_active: template.is_active,
        }
      : {
          ...emptyTemplateForm,
          variants: emptyTemplateForm.variants.map((variant) => ({
            ...variant,
          })),
        },
  );
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  const setField = (field: string, value: string | boolean) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const setVariantField = (
    index: number,
    field: keyof DripCampaignTemplateVariantConfig,
    value: string | boolean,
  ) =>
    setForm((prev) => ({
      ...prev,
      variants: prev.variants.map((variant, variantIndex) =>
        variantIndex === index ? { ...variant, [field]: value } : variant,
      ),
    }));

  const moveVariant = (index: number, direction: -1 | 1) => {
    setForm((prev) => {
      const target = index + direction;
      if (target < 0 || target >= prev.variants.length) return prev;
      const variants = [...prev.variants];
      [variants[index], variants[target]] = [variants[target], variants[index]];
      return { ...prev, variants };
    });
  };

  const removeVariant = (index: number) =>
    setForm((prev) => ({
      ...prev,
      variants: prev.variants.filter(
        (_variant, variantIndex) => variantIndex !== index,
      ),
    }));

  const addVariant = () =>
    setForm((prev) => ({
      ...prev,
      variants: [
        ...prev.variants,
        createEmptyVariant(
          nextVariantLabel(prev.variants),
          prev.variants.length,
        ),
      ],
    }));

  const normalizedVariantLabels = form.variants.map((variant) =>
    variant.label.trim().toLocaleLowerCase(),
  );
  const duplicateVariantLabels = new Set(
    normalizedVariantLabels.filter(
      (label, index) =>
        Boolean(label) && normalizedVariantLabels.indexOf(label) !== index,
    ),
  );
  const activeVariants = form.variants.filter((variant) => variant.is_active);
  const hasUsableActiveVariants =
    activeVariants.length > 0 && activeVariants.every(isValidVariant);
  const allVariantsHaveLabels = form.variants.every((variant) =>
    Boolean(variant.label.trim()),
  );
  const canSave = Boolean(
    form.name.trim() &&
      hasUsableActiveVariants &&
      allVariantsHaveLabels &&
      duplicateVariantLabels.size === 0,
  );

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    setError('');
    try {
      const payload = {
        name: form.name.trim(),
        campaign_type:
          form.campaign_type === GENERIC_CAMPAIGN_VALUE
            ? null
            : form.campaign_type,
        variants: form.variants.map((variant, index) => ({
          ...variant,
          label: variant.label.trim(),
          sort_order: index,
        })),
        notes: form.notes,
        is_active: form.is_active,
      };
      if (template) {
        await requestJson(
          'patch',
          apiEndpoints.salesConfigDripTemplate(template.id),
          payload,
        );
      } else {
        await requestJson(
          'post',
          apiEndpoints.salesConfigDripTemplates(),
          payload,
        );
      }
      onSaved();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!template) return;
    if (
      !window.confirm(
        `Delete the template "${template.name}"? This cannot be undone.`,
      )
    ) {
      return;
    }
    setDeleting(true);
    setError('');
    try {
      const response = await api.delete(
        apiEndpoints.salesConfigDripTemplate(template.id),
      );
      if (!response || !response.ok) {
        throw new Error(
          `Delete failed (status ${response ? response.status : 'unknown'})`,
        );
      }
      onDeleted?.();
    } catch (err) {
      setError(errorText(err));
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-4 border-t border-[rgb(var(--app-border)/0.72)] pt-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <Label>Template name (shown in the prospect dropdown)</Label>
          <input
            className={inputClassName}
            value={form.name}
            placeholder="e.g. Missed-calls opener"
            onChange={(e) => setField('name', e.target.value)}
          />
          {!form.name.trim() && (
            <p className="mt-1 text-xs text-red-600">Enter a template name.</p>
          )}
        </div>
        <div>
          <Label>Attached campaign type</Label>
          <select
            className={inputClassName}
            value={form.campaign_type}
            onChange={(e) => setField('campaign_type', e.target.value)}
          >
            <option value={GENERIC_CAMPAIGN_VALUE}>
              Generic (no campaign type)
            </option>
            {campaignTypes.map((campaign) => (
              <option key={campaign.key} value={campaign.key}>
                {campaign.label || campaign.key}
              </option>
            ))}
          </select>
        </div>
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400">
        Supported variables:{' '}
        {TEMPLATE_VARIABLES.map((variable) => (
          <code
            key={variable}
            className="mr-1 inline-block rounded bg-gray-100 px-1 py-0.5 font-mono text-[11px] dark:bg-white/[0.06]"
          >
            {variable}
          </code>
        ))}
      </p>
      <div className="space-y-4">
        {form.variants.map((variant, index) => {
          const missingLabel = !variant.label.trim();
          const duplicateLabel = duplicateVariantLabels.has(
            variant.label.trim().toLocaleLowerCase(),
          );
          const missingSubject = !variant.subject_template.trim();
          const missingEmailOne = !variant.email_1_template.trim();
          return (
            <div
              key={variant.id ?? `new-${index}`}
              className="rounded-2xl border border-[rgb(var(--app-border)/0.72)] p-4"
            >
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <h4 className="text-sm font-semibold">
                  Variant {variant.label.trim() || index + 1}
                </h4>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    className={secondaryButtonClassName}
                    onClick={() => moveVariant(index, -1)}
                    disabled={index === 0}
                    aria-label={`Move variant ${variant.label || index + 1} up`}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className={secondaryButtonClassName}
                    onClick={() => moveVariant(index, 1)}
                    disabled={index === form.variants.length - 1}
                    aria-label={`Move variant ${variant.label || index + 1} down`}
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    className={`${secondaryButtonClassName} text-red-600`}
                    onClick={() => removeVariant(index)}
                  >
                    Remove
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <Label>Variant label</Label>
                  <input
                    className={inputClassName}
                    value={variant.label}
                    placeholder="e.g. A"
                    onChange={(e) =>
                      setVariantField(index, 'label', e.target.value)
                    }
                  />
                  {missingLabel && (
                    <p className="mt-1 text-xs text-red-600">
                      Add a variant label.
                    </p>
                  )}
                  {duplicateLabel && (
                    <p className="mt-1 text-xs text-red-600">
                      Variant labels must be unique.
                    </p>
                  )}
                </div>
                <label className="flex items-center gap-2 self-end pb-2 text-sm">
                  <input
                    type="checkbox"
                    checked={variant.is_active}
                    onChange={(e) =>
                      setVariantField(index, 'is_active', e.target.checked)
                    }
                  />
                  Active (render for prospects)
                </label>
              </div>
              <div className="mt-4">
                <Label>Subject template</Label>
                <input
                  className={inputClassName}
                  value={variant.subject_template}
                  placeholder={
                    'e.g. ${restaurant.name}, stop missing event calls'
                  }
                  onChange={(e) =>
                    setVariantField(index, 'subject_template', e.target.value)
                  }
                />
                {variant.is_active && missingSubject && (
                  <p className="mt-1 text-xs text-red-600">
                    Active variants need a subject.
                  </p>
                )}
              </div>
              {(
                [
                  ['email_1_template', 'Email 1 template'],
                  ['email_2_template', 'Email 2 template'],
                  ['email_3_template', 'Email 3 template'],
                ] as const
              ).map(([field, label]) => (
                <div className="mt-4" key={field}>
                  <Label>{label}</Label>
                  <textarea
                    className={textAreaClassName}
                    rows={field === 'email_1_template' ? 5 : 4}
                    value={variant[field]}
                    onChange={(e) =>
                      setVariantField(index, field, e.target.value)
                    }
                  />
                  {variant.is_active &&
                    field === 'email_1_template' &&
                    missingEmailOne && (
                      <p className="mt-1 text-xs text-red-600">
                        Active variants need email 1 copy.
                      </p>
                    )}
                </div>
              ))}
            </div>
          );
        })}
        <button
          type="button"
          className={secondaryButtonClassName}
          onClick={addVariant}
        >
          + Add variant
        </button>
        {!hasUsableActiveVariants && (
          <p className="text-sm text-red-600">
            Add an active variant and complete every active variant&apos;s
            label, subject, and email 1.
          </p>
        )}
      </div>
      <div>
        <Label>Notes (internal)</Label>
        <textarea
          className={textAreaClassName}
          rows={2}
          value={form.notes}
          onChange={(e) => setField('notes', e.target.value)}
        />
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={form.is_active}
          onChange={(e) => setField('is_active', e.target.checked)}
        />
        Active (available on prospect Emails tabs)
      </label>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          className={primaryButtonClassName}
          onClick={save}
          disabled={saving || !canSave}
        >
          {saving ? 'Saving…' : template ? 'Save template' : 'Create template'}
        </button>
        {onCancel && (
          <button
            type="button"
            className={secondaryButtonClassName}
            onClick={onCancel}
          >
            Cancel
          </button>
        )}
        {template && (
          <button
            type="button"
            className={`${secondaryButtonClassName} text-red-600`}
            onClick={remove}
            disabled={deleting}
          >
            {deleting ? 'Deleting…' : 'Delete template'}
          </button>
        )}
      </div>
    </div>
  );
}

// ── Revenue assumptions editor ─────────────────────────────────────────────────

function RevenueAssumptionRow({
  assumption,
  onSaved,
}: {
  assumption: RevenueAssumptionConfig;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    missed_calls_per_month: String(assumption.missed_calls_per_month),
    high_value_inquiry_rate: assumption.high_value_inquiry_rate,
    average_inquiry_value: assumption.average_inquiry_value,
    close_rate: assumption.close_rate,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const estimate =
    (parseFloat(form.missed_calls_per_month) || 0) *
    (parseFloat(form.high_value_inquiry_rate) || 0) *
    (parseFloat(form.average_inquiry_value) || 0) *
    (parseFloat(form.close_rate) || 0) *
    12;

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      await requestJson(
        'patch',
        apiEndpoints.salesConfigRevenueAssumption(assumption.id),
        {
          missed_calls_per_month:
            parseInt(form.missed_calls_per_month, 10) || 0,
          high_value_inquiry_rate: form.high_value_inquiry_rate,
          average_inquiry_value: form.average_inquiry_value,
          close_rate: form.close_rate,
        },
      );
      onSaved();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setSaving(false);
    }
  };

  const numberField = (
    label: string,
    field: keyof typeof form,
    step: string,
  ) => (
    <div>
      <Label>{label}</Label>
      <input
        className={inputClassName}
        type="number"
        step={step}
        value={form[field]}
        onChange={(e) =>
          setForm((prev) => ({ ...prev, [field]: e.target.value }))
        }
      />
    </div>
  );

  return (
    <div className="rounded-2xl border border-[rgb(var(--app-border)/0.72)] p-4">
      <div className="mb-3 flex items-center justify-between">
        <span className="text-sm font-semibold capitalize">
          {assumption.tier}
        </span>
        <span className="text-sm text-gray-500">
          ≈ ${Math.round(estimate).toLocaleString()}/yr at risk
        </span>
      </div>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {numberField('Missed calls / month', 'missed_calls_per_month', '1')}
        {numberField(
          'High-value inquiry rate',
          'high_value_inquiry_rate',
          '0.01',
        )}
        {numberField('Avg inquiry value ($)', 'average_inquiry_value', '100')}
        {numberField('Close rate', 'close_rate', '0.05')}
      </div>
      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
      <button
        type="button"
        className={`${secondaryButtonClassName} mt-3`}
        onClick={save}
        disabled={saving}
      >
        {saving ? 'Saving…' : 'Save tier'}
      </button>
    </div>
  );
}

// ── Main tab ───────────────────────────────────────────────────────────────────

export default function SalesPlaybookTab() {
  const [campaignTypes, setCampaignTypes] = useState<CampaignTypeConfig[]>([]);
  const [playbooks, setPlaybooks] = useState<PlaybookConfig[]>([]);
  const [assumptions, setAssumptions] = useState<RevenueAssumptionConfig[]>([]);
  const [dripTemplates, setDripTemplates] = useState<
    DripCampaignTemplateConfig[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [expandedCampaign, setExpandedCampaign] = useState<number | null>(null);
  const [expandedTemplate, setExpandedTemplate] = useState<number | null>(null);
  const [creatingTemplate, setCreatingTemplate] = useState(false);
  const [selectedPlaybookId, setSelectedPlaybookId] = useState<number | null>(
    null,
  );

  // Segment strategy editing state
  const [ranking, setRanking] = useState<string[]>([]);
  const [enabledOverrides, setEnabledOverrides] = useState<
    Record<string, boolean>
  >({});
  const [seedIntros, setSeedIntros] = useState('');
  const [seedBodies, setSeedBodies] = useState('');
  const [extraInstructions, setExtraInstructions] = useState('');
  const [playbookSaving, setPlaybookSaving] = useState(false);
  const [playbookError, setPlaybookError] = useState('');
  const [draggedCampaignKey, setDraggedCampaignKey] = useState<string | null>(
    null,
  );

  // New playbook form
  const [newScopeType, setNewScopeType] = useState<'market' | 'cuisine'>(
    'market',
  );
  const [newScopeValue, setNewScopeValue] = useState('');

  const loadAll = useCallback(async () => {
    setLoadError('');
    try {
      const [types, books, revs, templates] = await Promise.all([
        requestJson<CampaignTypeConfig[]>(
          'get',
          apiEndpoints.salesConfigCampaignTypes(),
        ),
        requestJson<PlaybookConfig[]>(
          'get',
          apiEndpoints.salesConfigPlaybooks(),
        ),
        requestJson<RevenueAssumptionConfig[]>(
          'get',
          apiEndpoints.salesConfigRevenueAssumptions(),
        ),
        requestJson<DripCampaignTemplateConfig[]>(
          'get',
          apiEndpoints.salesConfigDripTemplates(),
        ),
      ]);
      setCampaignTypes(types);
      setPlaybooks(books);
      setAssumptions(revs);
      setDripTemplates(templates);
      setSelectedPlaybookId((prev) => {
        if (prev && books.some((b) => b.id === prev)) return prev;
        const globalBook = books.find((b) => b.scope_type === 'global');
        return globalBook?.id ?? books[0]?.id ?? null;
      });
    } catch (err) {
      setLoadError(errorText(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const selectedPlaybook = useMemo(
    () => playbooks.find((b) => b.id === selectedPlaybookId) || null,
    [playbooks, selectedPlaybookId],
  );

  // Sync segment editing state when the selected playbook changes.
  useEffect(() => {
    if (!selectedPlaybook) return;
    const known = campaignTypes.map((c) => c.key);
    const ranked = selectedPlaybook.campaign_type_ranking.filter((k) =>
      known.includes(k),
    );
    const missing = known.filter((k) => !ranked.includes(k));
    setRanking([...ranked, ...missing]);
    setEnabledOverrides(selectedPlaybook.enabled_overrides || {});
    setSeedIntros(listToText(selectedPlaybook.seed_intros));
    setSeedBodies(listToText(selectedPlaybook.seed_bodies));
    setExtraInstructions(selectedPlaybook.extra_instructions || '');
    setPlaybookError('');
  }, [selectedPlaybook, campaignTypes]);

  const moveRank = (index: number, direction: -1 | 1) => {
    setRanking((prev) => {
      const target = index + direction;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const moveRankToIndex = (fromKey: string, toIndex: number) => {
    setRanking((prev) => {
      const fromIndex = prev.indexOf(fromKey);
      if (fromIndex === -1 || fromIndex === toIndex) return prev;
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  };

  const handleRankDragStart = (
    event: React.DragEvent<HTMLDivElement>,
    key: string,
  ) => {
    setDraggedCampaignKey(key);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', key);
  };

  const handleRankDrop = (
    event: React.DragEvent<HTMLDivElement>,
    toIndex: number,
  ) => {
    event.preventDefault();
    const fromKey =
      draggedCampaignKey || event.dataTransfer.getData('text/plain');
    if (!fromKey) return;
    moveRankToIndex(fromKey, toIndex);
    setDraggedCampaignKey(null);
  };

  const savePlaybook = async () => {
    if (!selectedPlaybook) return;
    setPlaybookSaving(true);
    setPlaybookError('');
    try {
      await requestJson(
        'patch',
        apiEndpoints.salesConfigPlaybook(selectedPlaybook.id),
        {
          campaign_type_ranking: ranking,
          enabled_overrides: enabledOverrides,
          seed_intros: textToList(seedIntros),
          seed_bodies: textToList(seedBodies),
          extra_instructions: extraInstructions,
        },
      );
      await loadAll();
    } catch (err) {
      setPlaybookError(errorText(err));
    } finally {
      setPlaybookSaving(false);
    }
  };

  const createPlaybook = async () => {
    if (!newScopeValue.trim()) return;
    setPlaybookError('');
    try {
      const created = await requestJson<PlaybookConfig>(
        'post',
        apiEndpoints.salesConfigPlaybooks(),
        {
          scope_type: newScopeType,
          scope_value: newScopeValue.trim(),
          campaign_type_ranking: campaignTypes.map((c) => c.key),
        },
      );
      setNewScopeValue('');
      await loadAll();
      setSelectedPlaybookId(created.id);
    } catch (err) {
      setPlaybookError(errorText(err));
    }
  };

  const campaignByKey = useMemo(() => {
    const map: Record<string, CampaignTypeConfig> = {};
    campaignTypes.forEach((c) => {
      map[c.key] = c;
    });
    return map;
  }, [campaignTypes]);

  const scopedAssumptions = useMemo(() => {
    if (!selectedPlaybook) return [];
    const scoped = assumptions.filter(
      (a) => a.playbook === selectedPlaybook.id,
    );
    if (scoped.length > 0) return scoped;
    return assumptions.filter((a) => a.playbook === null);
  }, [assumptions, selectedPlaybook]);

  if (loading) {
    return (
      <ComponentCard title="Sales Playbook">
        <p className="text-sm text-gray-500">Loading playbook configuration…</p>
      </ComponentCard>
    );
  }

  if (loadError) {
    return (
      <ComponentCard title="Sales Playbook">
        <p className="text-sm text-red-600">{loadError}</p>
        <button
          type="button"
          className={secondaryButtonClassName}
          onClick={loadAll}
        >
          Retry
        </button>
      </ComponentCard>
    );
  }

  return (
    <div className="space-y-6">
      <ComponentCard
        title="Campaign stack-ranking per segment"
        description="Stack-rank campaign types per segment. The research agent picks the highest-ranked campaign with credible evidence — a high rank never wins without evidence."
        collapsible
      >
        <div className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label>Playbook segment</Label>
              <select
                className={inputClassName}
                value={selectedPlaybookId ?? ''}
                onChange={(e) => setSelectedPlaybookId(Number(e.target.value))}
              >
                {playbooks.map((book) => (
                  <option key={book.id} value={book.id}>
                    {book.scope_type === 'global'
                      ? 'Global default'
                      : `${book.scope_type}: ${book.scope_value}`}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end gap-2">
              <div>
                <Label>New segment</Label>
                <select
                  className={inputClassName}
                  value={newScopeType}
                  onChange={(e) =>
                    setNewScopeType(e.target.value as 'market' | 'cuisine')
                  }
                >
                  <option value="market">Market</option>
                  <option value="cuisine">Cuisine</option>
                </select>
              </div>
              <div>
                <Label>Value</Label>
                <input
                  className={inputClassName}
                  placeholder="e.g. NYC or Mexican"
                  value={newScopeValue}
                  onChange={(e) => setNewScopeValue(e.target.value)}
                />
              </div>
              <button
                type="button"
                className={secondaryButtonClassName}
                onClick={createPlaybook}
                disabled={!newScopeValue.trim()}
              >
                Add segment
              </button>
            </div>
          </div>

          {selectedPlaybook && (
            <>
              <div>
                <Label>Campaign stack ranking (top = highest priority)</Label>
                <div className="mt-1 space-y-1.5">
                  {ranking.map((key, index) => {
                    const campaign = campaignByKey[key];
                    const enabled =
                      enabledOverrides[key] ?? campaign?.is_active ?? true;
                    return (
                      <div
                        key={key}
                        draggable
                        onDragStart={(event) => handleRankDragStart(event, key)}
                        onDragOver={(event) => event.preventDefault()}
                        onDrop={(event) => handleRankDrop(event, index)}
                        onDragEnd={() => setDraggedCampaignKey(null)}
                        className={`flex cursor-grab items-center gap-3 rounded-2xl border border-[rgb(var(--app-border)/0.72)] px-3 py-2 transition hover:border-[rgb(var(--app-brand)/0.5)] active:cursor-grabbing ${
                          enabled ? '' : 'opacity-50'
                        } ${
                          draggedCampaignKey === key
                            ? 'border-[rgb(var(--app-brand)/0.8)] bg-[rgb(var(--app-brand)/0.06)] opacity-70'
                            : ''
                        }`}
                      >
                        <span
                          className="text-gray-300 dark:text-gray-600"
                          aria-hidden="true"
                        >
                          ⋮⋮
                        </span>
                        <span className="w-6 text-right text-sm font-semibold text-gray-400">
                          {index + 1}
                        </span>
                        <div className="min-w-0 flex-1">
                          <span className="text-sm font-medium">
                            {campaign?.label || key}
                          </span>
                          <span className="ml-2 text-xs text-gray-400">
                            {key}
                          </span>
                        </div>
                        <label className="flex items-center gap-1.5 text-xs text-gray-500">
                          <input
                            type="checkbox"
                            checked={enabled}
                            onChange={(e) =>
                              setEnabledOverrides((prev) => ({
                                ...prev,
                                [key]: e.target.checked,
                              }))
                            }
                          />
                          Enabled
                        </label>
                        <div className="flex gap-1">
                          <button
                            type="button"
                            className={secondaryButtonClassName}
                            onClick={() => moveRank(index, -1)}
                            disabled={index === 0}
                            aria-label={`Move ${key} up`}
                          >
                            ↑
                          </button>
                          <button
                            type="button"
                            className={secondaryButtonClassName}
                            onClick={() => moveRank(index, 1)}
                            disabled={index === ranking.length - 1}
                            aria-label={`Move ${key} down`}
                          >
                            ↓
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <Label>
                    Seed intros (one per line, fed to research + drip agents)
                  </Label>
                  <textarea
                    className={textAreaClassName}
                    rows={4}
                    value={seedIntros}
                    onChange={(e) => setSeedIntros(e.target.value)}
                  />
                </div>
                <div>
                  <Label>Seed email bodies (one per line)</Label>
                  <textarea
                    className={textAreaClassName}
                    rows={4}
                    value={seedBodies}
                    onChange={(e) => setSeedBodies(e.target.value)}
                  />
                </div>
              </div>
              <div>
                <Label>Extra instructions for the research agent</Label>
                <textarea
                  className={textAreaClassName}
                  rows={2}
                  value={extraInstructions}
                  onChange={(e) => setExtraInstructions(e.target.value)}
                />
              </div>
              {playbookError && (
                <p className="text-sm text-red-600">{playbookError}</p>
              )}
              <button
                type="button"
                className={primaryButtonClassName}
                onClick={savePlaybook}
                disabled={playbookSaving}
              >
                {playbookSaving ? 'Saving…' : 'Save stack ranking'}
              </button>
            </>
          )}
        </div>
      </ComponentCard>

      <ComponentCard
        title="Campaign strategy"
        description="The campaign catalog: what each angle means, what evidence qualifies it, example copy, and claim-safety rules fed to the agents."
        collapsible
      >
        <div className="space-y-3">
          {campaignTypes.map((campaign) => (
            <div
              key={campaign.id}
              className="rounded-2xl border border-[rgb(var(--app-border)/0.72)] p-4"
            >
              <button
                type="button"
                className="flex w-full items-center justify-between text-left"
                onClick={() =>
                  setExpandedCampaign((prev) =>
                    prev === campaign.id ? null : campaign.id,
                  )
                }
              >
                <div className="min-w-0">
                  <span className="text-sm font-semibold">
                    {campaign.label}
                  </span>
                  <span className="ml-2 text-xs text-gray-400">
                    {campaign.key}
                  </span>
                  {!campaign.is_active && (
                    <span className="ml-2 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs text-amber-700 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
                      Disabled
                    </span>
                  )}
                  {campaign.revenue_relevant && (
                    <span className="ml-2 rounded-full border border-green-200 bg-green-50 px-2 py-0.5 text-xs text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:text-green-300">
                      Revenue
                    </span>
                  )}
                </div>
                <span className="text-xs text-gray-400">
                  v{campaign.version} ·{' '}
                  {expandedCampaign === campaign.id ? 'Collapse' : 'Edit'}
                </span>
              </button>
              {expandedCampaign === campaign.id && (
                <CampaignTypeEditor campaign={campaign} onSaved={loadAll} />
              )}
            </div>
          ))}
        </div>
      </ComponentCard>

      <ComponentCard
        title="Drip campaign templates"
        description="Named, reusable email sequences rendered per prospect with dynamic variables. Templates appear by name in the Template drip dropdown on a prospect's Emails tab. Attach one to a campaign type for strategy context, or keep it generic. Missing variables render blank with a warning."
        collapsible
      >
        <div className="space-y-3">
          {dripTemplates.length === 0 && !creatingTemplate && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No templates yet. Create one to make it selectable on prospect
              Emails tabs.
            </p>
          )}
          {dripTemplates.map((template) => (
            <div
              key={template.id}
              className="rounded-2xl border border-[rgb(var(--app-border)/0.72)] p-4"
            >
              <button
                type="button"
                className="flex w-full items-center justify-between text-left"
                onClick={() =>
                  setExpandedTemplate((prev) =>
                    prev === template.id ? null : template.id,
                  )
                }
              >
                <div className="min-w-0">
                  <span className="text-sm font-semibold">{template.name}</span>
                  <span className="ml-2 text-xs text-gray-400">
                    {template.campaign_type
                      ? template.campaign_type_label || template.campaign_type
                      : 'Generic'}
                  </span>
                  {!template.is_active && (
                    <span className="ml-2 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs text-amber-700 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
                      Inactive
                    </span>
                  )}
                </div>
                <span className="text-xs text-gray-400">
                  v{template.version} ·{' '}
                  {expandedTemplate === template.id ? 'Collapse' : 'Edit'}
                </span>
              </button>
              {expandedTemplate === template.id && (
                <DripTemplateEditor
                  key={`${template.id}-v${template.version}`}
                  template={template}
                  campaignTypes={campaignTypes}
                  onSaved={loadAll}
                  onDeleted={() => {
                    setExpandedTemplate(null);
                    loadAll();
                  }}
                />
              )}
            </div>
          ))}
          {creatingTemplate ? (
            <div className="rounded-2xl border border-dashed border-[rgb(var(--app-border)/0.72)] p-4">
              <h4 className="text-sm font-semibold text-[rgb(var(--app-text))]">
                New template
              </h4>
              <DripTemplateEditor
                template={null}
                campaignTypes={campaignTypes}
                onSaved={() => {
                  setCreatingTemplate(false);
                  loadAll();
                }}
                onCancel={() => setCreatingTemplate(false)}
              />
            </div>
          ) : (
            <button
              type="button"
              className={secondaryButtonClassName}
              onClick={() => setCreatingTemplate(true)}
            >
              + New template
            </button>
          )}
        </div>
      </ComponentCard>

      <ComponentCard
        title="Revenue assumptions"
        description="Inputs for revenue-at-risk math: missed calls x high-value inquiry rate x avg value x close rate x 12. The agent picks the tier matching evidence strength and always frames results as estimates."
        collapsible
      >
        <div className="space-y-3">
          {selectedPlaybook &&
            scopedAssumptions.length > 0 &&
            scopedAssumptions[0].playbook === null &&
            selectedPlaybook.scope_type !== 'global' && (
              <p className="text-xs text-gray-500">
                This segment has no overrides; showing the global defaults.
              </p>
            )}
          {scopedAssumptions.map((assumption) => (
            <RevenueAssumptionRow
              key={assumption.id}
              assumption={assumption}
              onSaved={loadAll}
            />
          ))}
        </div>
      </ComponentCard>
    </div>
  );
}
